import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Stack,
  IconButton,
} from "@mui/material";
import { AddCircle, Edit } from "@mui/icons-material";
import {
  createUIColorFont,
  getAllUIColorFont,
  updateUIColorFont,
} from "../../State/UIColorFont/Action";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { PermissionService } from "../../../config/permissionService";

const fontSizes = ["6px", "8px", "10px", "12px", "14px", "16px", "18px", "20px", "22px", "24px", "26px", "28px", "30px", "32px", "34px", "36px", "38px", "40px"];
const fontFamilies = ["Arial", "Georgia", "Courier New", "Verdana", "Helvetica", "Helvetica Bold Oblique", "sans-serif", "Greycliff CF", "Segoe UI", "Source Sans Pro", "Times",
  "Times Bold",
  "Times Bold Italic",
  "Times Italic",
  "Times New Roman", "Roboto",
  "Roboto Black",];

const ManageUIColorFont = () => {
  const dispatch = useDispatch();
  const { adminUIColorFonts } = useSelector((store) => store);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    type: "",
    colorCode: "",
    fontSize: "",
    fontFamily: "",
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(getAllUIColorFont());
  }, [dispatch]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if (!form.type || !form.colorCode || !form.fontSize || !form.fontFamily) {
      toast.error("Please fill all fields");
      return;
    }

    if (editingId) {
      dispatch(updateUIColorFont(editingId, form));
    } else {
      dispatch(createUIColorFont(form));
    }

    setForm({
      type: "",
      colorCode: "",
      fontSize: "",
      fontFamily: "",
    });
    setEditingId(null);
    setOpen(false);
  };

  const handleEdit = (item) => {
    setForm({
      type: item.type,
      colorCode: item.colorCode,
      fontSize: item.fontSize,
      fontFamily: item.fontFamily,
    });
    setEditingId(item.id);
    setOpen(true);
  };

  const openFormWithType = (type) => {
    setForm({
      type,
      colorCode: "",
      fontSize: "",
      fontFamily: "",
    });
    setEditingId(null);
    setOpen(true);
  };

  const writeAction = PermissionService.has({ category: "Admin", page: "Manage UI Color Font", type: "WRITE" });

  const renderCard = (label, type) => {
    const filtered =
      adminUIColorFonts?.uiColorFont?.response?.filter(
        (item) => item.type === type
      ) || [];

    return (
      <Card sx={{ mb: 2, borderRadius: 2, position: "relative" }}>
        <CardHeader
          title={<Typography variant="h6">{label}</Typography>}
          action={
            filtered.length === 0 && writeAction && (
              <IconButton onClick={() => openFormWithType(type)}>
                <AddCircle color="primary" fontSize="small" />
              </IconButton>
            )
          }
        />
        <CardContent>
          {filtered.length > 0 ? (
            filtered.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  p: 2,
                  mb: 1,
                  background: "#f9f9f9",
                  position: "relative",
                }}
              >
                {/* Small edit icon in top right corner */}
                {writeAction && (<IconButton
                  onClick={() => handleEdit(item)}
                  size="small"
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <Edit fontSize="small" />
                </IconButton>
                )}
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">Color Code:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {item.colorCode}
                    </Typography>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        bgcolor: item.colorCode,
                        borderRadius: 1,
                      }}
                    />
                  </Stack>
                  <Typography variant="body2">
                    Font Size: <strong>{item.fontSize}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Font Family: <strong>{item.fontFamily}</strong>
                  </Typography>
                </Stack>
              </Box>
            ))
          ) : (
            <Typography>
              {label.toLowerCase()} color, font size, and family not found
            </Typography>
          )}
        </CardContent>
      </Card>

    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardHeader
          title={<Typography variant="h6">Manage UI Elements</Typography>}
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>{renderCard("Heading", "heading")}</Grid>
            <Grid item xs={12} md={4}>{renderCard("Title", "title")}</Grid>
            <Grid item xs={12} md={4}>{renderCard("Description", "description")}</Grid>
          </Grid>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Update UI Element" : "Add UI Element"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Type"
                name="type"
                value={form.type}
                onChange={handleChange}
                fullWidth
                disabled
              >
                <MenuItem value="heading">Heading</MenuItem>
                <MenuItem value="title">Title</MenuItem>
                <MenuItem value="description">Description</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="color"
                label="Color Code"
                name="colorCode"
                value={form.colorCode}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Font Size"
                name="fontSize"
                value={form.fontSize}
                onChange={handleChange}
                fullWidth
              >
                {fontSizes.map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Font Family"
                name="fontFamily"
                value={form.fontFamily}
                onChange={handleChange}
                fullWidth
              >
                {fontFamilies.map((font) => (
                  <MenuItem key={font} value={font}>{font}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? "Update" : "Add"}
          </Button>
        </DialogActions>

      </Dialog>
    </Box>
  );
};

export default ManageUIColorFont;
